/**
 * Guard Shift Reassign Page - Microsoft 365 Style
 * Reassign guards to different shifts
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { makeStyles, Text, Button, Input, Field, Avatar, tokens } from '@fluentui/react-components';
import { ArrowLeftRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { SearchableDropdown } from '../../../components/common';
import { ApiClient, newToast, getEmployeeImageUrl } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  guardInfoSection: {
    marginBottom: '24px',
  },
  guardInfoTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '16px',
    textAlign: 'center',
  },
  avatarContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '16px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  infoLabel: {
    fontSize: '12px',
    color: '#605e5c',
    fontWeight: '600',
  },
  infoValue: {
    fontSize: '14px',
    color: '#0078d4',
    fontWeight: '400',
  },
  formSection: {
    marginBottom: '24px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '16px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
});

interface Client {
  id: string;
  client_name: string;
}

interface Site {
  id: string;
  name: string;
  site_code?: string;
}

interface Shift {
  id: string;
  name: string;
  start_time: string;
  end_time: string;
  clock_count?: number;
}

interface GuardInfo {
  id: string;
  employee_id: string;
  first_name: string;
  other_names?: string;
  last_name: string;
  date_of_birth?: string;
  email?: string;
  phone?: string;
  current_site_id: string;
  current_shift_id: string;
  current_client_id: string;
  start_date?: string;
  face_url?: string;
}

export const GuardShiftReassignFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const assignmentData = location.state as GuardInfo | undefined;

  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);

  const [formData, setFormData] = useState<{
    client_id: string;
    site_id: string;
    shift_id: string;
    start_date: string;
  }>({
    client_id: '',
    site_id: '',
    shift_id: '',
    start_date: '',
  });

  const selectedSite = sites.find(s => s.id.toString() === formData.site_id);
  const selectedShift = shifts.find(s => s.id.toString() === formData.shift_id);

  useEffect(() => {
    if (!assignmentData) {
      newToast('error', 'No assignment data provided');
      navigate('/account/guard-operations/assign-guard');
      return;
    }

    setFormData({
      client_id: assignmentData.current_client_id || '',
      site_id: assignmentData.current_site_id || '',
      shift_id: assignmentData.current_shift_id || '',
      start_date: (assignmentData.start_date || new Date().toISOString().split('T')[0]) as string,
    });

    loadClients();
    loadShifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formData.client_id) {
      loadSites(formData.client_id);
    } else {
      setSites([]);
    }
  }, [formData.client_id]);

  const loadClients = async () => {
    try {
      const response = await ApiClient.get('/get-clients');
      if (response.data?.status === 'success') {
        setClients(response.data?.data || []);
      }
    } catch (error) {
      newToast('error', `Failed to load clients: ${getErrorMessage(error)}`);
    }
  };

  const loadSites = async (clientId: string) => {
    try {
      const response = await ApiClient.get(`/get-sites?client_id=${clientId}`);
      if (response.data?.status === 'success') {
        setSites(response.data?.data || []);
      }
    } catch (error) {
      newToast('error', `Failed to load sites: ${getErrorMessage(error)}`);
    }
  };

  const loadShifts = async () => {
    try {
      const response = await ApiClient.get('/get-shifts');
      if (response.data?.status === 'success') {
        setShifts(response.data?.data || []);
      }
    } catch (error) {
      newToast('error', `Failed to load shifts: ${getErrorMessage(error)}`);
    }
  };

  const calculateHours = (): number => {
    if (!selectedShift) {
      return 0;
    }
    const startParts = selectedShift.start_time.split(':').map(Number);
    const endParts = selectedShift.end_time.split(':').map(Number);

    const startHour: number = startParts[0] ?? 0;
    const startMin: number = startParts[1] ?? 0;
    const endHour: number = endParts[0] ?? 0;
    const endMin: number = endParts[1] ?? 0;

    if (isNaN(startHour) || isNaN(startMin) || isNaN(endHour) || isNaN(endMin)) {
      return 0;
    }
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const diffMinutes =
      endMinutes >= startMinutes ? endMinutes - startMinutes : 1440 - startMinutes + endMinutes;
    return Math.round((diffMinutes / 60) * 10) / 10;
  };

  const handleReassign = async () => {
    if (!assignmentData) {
      return;
    }

    if (!formData.client_id || !formData.site_id || !formData.shift_id || !formData.start_date) {
      newToast('error', 'Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id: assignmentData.id,
        employee_id: assignmentData.employee_id,
        client_id: formData.client_id,
        site_id: formData.site_id,
        shift_id: formData.shift_id,
        start_date: formData.start_date,
      };

      const response = await ApiClient.post('/add-update-assign-guard', payload);
      if (response.data?.status === 'success') {
        newToast('success', 'Guard reassigned successfully');
        navigate('/account/guard-operations/assign-guard');
      } else {
        newToast('error', response.data?.message || 'Failed to reassign guard');
      }
    } catch (error) {
      newToast('error', `Error: ${getErrorMessage(error)}`);
    } finally {
      setLoading(false);
    }
  };

  if (!assignmentData) {
    return null;
  }

  return (
    <div>
      <div className={styles.pageHeader}>
        <Button
          appearance="subtle"
          icon={<ArrowLeftRegular />}
          onClick={() => navigate('/account/guard-operations/assign-guard')}
        />
        <Text className={styles.pageTitle}>Reassign Guard</Text>
      </div>

      <ContentCard>
        <div className={styles.guardInfoSection}>
          <Text className={styles.guardInfoTitle}>Guard Information</Text>

          <div className={styles.avatarContainer}>
            <Avatar
              size={96}
              name={`${assignmentData.first_name} ${assignmentData.last_name}`}
              image={
                assignmentData.face_url
                  ? { src: getEmployeeImageUrl(assignmentData.face_url) }
                  : undefined
              }
              color="colorful"
            />
          </div>

          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <Text className={styles.infoLabel}>First Name</Text>
              <Text className={styles.infoValue}>{assignmentData.first_name}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.infoLabel}>Other Names</Text>
              <Text className={styles.infoValue}>{assignmentData.other_names || '-'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.infoLabel}>Last Name</Text>
              <Text className={styles.infoValue}>{assignmentData.last_name}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.infoLabel}>Date Of Birth</Text>
              <Text className={styles.infoValue}>{assignmentData.date_of_birth || '-'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.infoLabel}>Email</Text>
              <Text className={styles.infoValue}>{assignmentData.email || '-'}</Text>
            </div>
            <div className={styles.infoItem}>
              <Text className={styles.infoLabel}>Phone Number</Text>
              <Text className={styles.infoValue}>{assignmentData.phone || '-'}</Text>
            </div>
          </div>

          <div className={styles.infoItem}>
            <Text className={styles.infoLabel}>Employee ID</Text>
            <Text className={styles.infoValue}>{assignmentData.employee_id}</Text>
          </div>
        </div>

        <div className={styles.formSection}>
          <div className={styles.formRow}>
            <Field label="Client" required>
              <SearchableDropdown
                placeholder="Search client..."
                options={clients.map(c => ({ id: c.id, label: c.client_name }))}
                selectedValue={formData.client_id}
                onSelect={value => {
                  setFormData({ ...formData, client_id: value, site_id: '' });
                }}
              />
            </Field>

            <Field label="Client Site" required>
              <SearchableDropdown
                placeholder="Search site..."
                options={sites.map(s => ({ id: s.id, label: s.name }))}
                selectedValue={formData.site_id}
                onSelect={value => setFormData({ ...formData, site_id: value })}
                disabled={!formData.client_id}
              />
            </Field>

            <Field label="Site Code">
              <Input value={selectedSite?.site_code || ''} readOnly disabled />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field label="Shift" required>
              <SearchableDropdown
                placeholder="Search shift..."
                options={shifts.map(s => ({ id: s.id, label: s.name }))}
                selectedValue={formData.shift_id}
                onSelect={value => setFormData({ ...formData, shift_id: value })}
              />
            </Field>

            <Field label="Start Time">
              <Input value={selectedShift?.start_time || ''} readOnly disabled />
            </Field>

            <Field label="End Time">
              <Input value={selectedShift?.end_time || ''} readOnly disabled />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field label="Hours">
              <Input value={calculateHours().toString()} readOnly disabled />
            </Field>

            <Field label="Required Clock Count">
              <Input value={selectedShift?.clock_count?.toString() || '4'} readOnly disabled />
            </Field>

            <Field label="Start Date" required>
              <Input
                type="date"
                value={formData.start_date}
                onChange={e => setFormData({ ...formData, start_date: e.target.value })}
              />
            </Field>
          </div>
        </div>

        <div className={styles.actions}>
          <Button
            appearance="primary"
            onClick={handleReassign}
            disabled={loading}
            size="large"
            style={{ minWidth: '200px' }}
          >
            {loading ? 'Reassigning...' : 'Reassign'}
          </Button>
        </div>
      </ContentCard>
    </div>
  );
};

export default GuardShiftReassignFluentUI;
